import {
  athenaQuery,
  AFFILIATION_ID,
  COUNTRY,
  haversineMiles,
  mapScreenTags,
  isTheaterOpen,
} from './AthenaApi';
import {
  getFilmsAndShowtimesResult,
  mapAthenaMovie,
  mapAthenaShowtimes,
  MOVIE_FIELDS,
  buildTagsFilter,
} from './GetFilmsAndShowtimes';

export type Cinema = {
  id: string;
  location: {
    address: string;
    city: string;
    region: string;
    zip: string;
  };
  name: string;
  screens: Array<{
    accessibility: string[];
    name: string;
  }>;
  tags: {
    list: string[];
  };
  coordinates: {
    latitude: number | null;
    longitude: number | null;
    distance: string;
  };
  url: string;
  email: string;
  phone: string;
  description: string;
};

export type getCinemaParameters = {
  searchTerm: string | null;
  // Location search: searched-from point + radius (km, already converted from miles).
  location: { lat: number; lon: number } | null;
  maxDistanceKm: number | null;
  apiLimit: number;
};

// Normalize an Athena theater node into the legacy { node: Cinema } shape so all
// consumers (`cinema.node.X`) keep working. Distance is computed client-side.
const toCinemaNode = (
  node: any,
  location: { lat: number; lon: number } | null
): { node: Cinema } => {
  const coords = node.geocoordinates;
  let distance = '';
  if (location && coords) {
    distance = haversineMiles(
      location.lat,
      location.lon,
      coords.latitude,
      coords.longitude
    ).toFixed(1);
  }

  return {
    node: {
      id: node.id,
      location: {
        address: node.address ? node.address.address : '',
        city: node.address ? node.address.city : '',
        region: node.address ? node.address.state : '',
        zip: node.address ? node.address.zip : '',
      },
      name: node.name,
      screens: (node.screens || []).map((screen: any) => ({
        accessibility: mapScreenTags(screen.tags),
        name: screen.name,
      })),
      tags: { list: node.tags || [] },
      coordinates: {
        latitude: coords ? coords.latitude : null,
        longitude: coords ? coords.longitude : null,
        distance,
      },
      url: node.url,
      email: node.email,
      phone: node.phoneNumber,
      description: node.description,
    },
  };
};

// Build the `theaters` location/text filter fragment shared by the queries below.
const buildTheaterFilterArgs = (
  searchTerm: string | null,
  location: { lat: number; lon: number } | null,
  maxDistanceKm: number | null
): string => {
  if (location && maxDistanceKm !== null) {
    return `
      aroundCoords: { latitude: ${location.lat}, longitude: ${location.lon} },
      maxDistance: ${maxDistanceKm}
    `;
  }
  if (searchTerm) {
    return `search: "${searchTerm}"`;
  }
  return '';
};

export const getCinemas = (
  parameters: getCinemaParameters
): Promise<Array<{ node: Cinema }>> => {
  const filterArgs = buildTheaterFilterArgs(
    parameters.searchTerm,
    parameters.location,
    parameters.maxDistanceKm
  );

  const results: Array<{ node: Cinema }> = [];

  const makeCall = (offset: number): Promise<Array<{ node: Cinema }>> => {
    const query = `
      query GetCinemas {
        theaters(
          affiliationId: "${AFFILIATION_ID}",
          iso_3166_1_a2: "${COUNTRY}",
          limit: ${parameters.apiLimit},
          offset: ${offset}
          ${filterArgs ? `, ${filterArgs}` : ''}
        ) {
          totalCount
          nodes {
            id
            name
            url
            email
            phoneNumber
            description(locale: "en_GB")
            address { address city state zip }
            geocoordinates { latitude longitude }
            tags
            screens { name tags }
            opening { status }
          }
        }
      }`;

    return athenaQuery(query, 'theaters').then((r: any) => {
      if (r === null) {
        return results;
      }

      const { totalCount, nodes } = r.data.theaters;
      nodes.filter(isTheaterOpen).forEach((node: any) => {
        results.push(toCinemaNode(node, parameters.location));
      });

      if (offset + parameters.apiLimit < totalCount) {
        return makeCall(offset + parameters.apiLimit);
      }

      return results;
    });
  };

  return makeCall(0).catch(() => []);
};

export type CinemaWithShowtimes = {
  id: string;
  name: string;
  url: string;
  coordinates: {
    latitude: number | null;
    longitude: number | null;
    distance: string;
  };
  films: Array<getFilmsAndShowtimesResult>;
};

export type getCinemasWithShowtimesParameters = getCinemaParameters & {
  fromDate: string;
  toDate: string;
  showtimeTags: string[];
};

// Fetch the scoped cinemas AND each one's schedule in a single (paginated) query,
// via `theaters { nodes { ... schedule {...} } }`. This is the film-finder's lookup:
// it replaces one schedule query per nearby cinema with one query per page of cinemas.
export const getCinemasWithShowtimes = (
  parameters: getCinemasWithShowtimesParameters
): Promise<Array<CinemaWithShowtimes>> => {
  const filterArgs = buildTheaterFilterArgs(
    parameters.searchTerm,
    parameters.location,
    parameters.maxDistanceKm
  );
  const tagsFilter = buildTagsFilter('tags', parameters.showtimeTags);

  const results: Array<CinemaWithShowtimes> = [];

  const makeCall = (offset: number): Promise<Array<CinemaWithShowtimes>> => {
    const query = `
      query GetCinemasWithShowtimes {
        theaters(
          affiliationId: "${AFFILIATION_ID}",
          iso_3166_1_a2: "${COUNTRY}",
          limit: ${parameters.apiLimit},
          offset: ${offset}
          ${filterArgs ? `, ${filterArgs}` : ''}
        ) {
          totalCount
          nodes {
            id
            name
            url
            geocoordinates { latitude longitude }
            opening { status }
            schedule(
              from: "${parameters.fromDate}",
              to: "${parameters.toDate}"
              ${tagsFilter ? `, ${tagsFilter}` : ''}
            ) {
              nodes {
                movie {
                  ${MOVIE_FIELDS}
                }
                showtimes {
                  startsAt
                  attributes
                  ticketingUrl
                }
              }
            }
          }
        }
      }`;

    return athenaQuery(query, 'theaters').then((r: any) => {
      if (r === null) {
        return results;
      }

      const { totalCount, nodes } = r.data.theaters;
      nodes.filter(isTheaterOpen).forEach((node: any) => {
        const coords = node.geocoordinates;
        let distance = '';
        if (parameters.location && coords) {
          distance = haversineMiles(
            parameters.location.lat,
            parameters.location.lon,
            coords.latitude,
            coords.longitude
          ).toFixed(1);
        }

        const scheduleNodes =
          node.schedule && node.schedule.nodes ? node.schedule.nodes : [];

        results.push({
          id: node.id,
          name: node.name,
          url: node.url,
          coordinates: {
            latitude: coords ? coords.latitude : null,
            longitude: coords ? coords.longitude : null,
            distance,
          },
          films: scheduleNodes.map((scheduleNode: any) => ({
            movie: mapAthenaMovie(scheduleNode.movie),
            showtimes: mapAthenaShowtimes(scheduleNode.showtimes),
          })),
        });
      });

      if (offset + parameters.apiLimit < totalCount) {
        return makeCall(offset + parameters.apiLimit);
      }

      return results;
    });
  };

  return makeCall(0).catch(() => []);
};
